import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useVideoAnalytics } from '@libertify.frontend/analytics';
import { useScormTracking } from '@libertify/scorm';
import Player from 'video.js/dist/types/player';
import type { IHighlight } from 'react-pdf-highlighter';
import { useAnalytics } from './useAnalytics';
import { handleShowHeader } from '../helpers';
import {
  Cuepoint,
  CustomTheme,
  DEFAULT_PLAYBACK_RATES,
  Layout,
  MediaAnalyticsEvents,
  VideoJsOptions,
} from '../types';

type ScormUrlConfig = {
  enabled: boolean;
  disableSeek: boolean;
  threshold: number;
  pauseOnBlur: boolean;
};

function readScormConfigFromUrl(): ScormUrlConfig {
  if (typeof globalThis.window === 'undefined') {
    return {
      enabled: false,
      disableSeek: false,
      threshold: 1,
      pauseOnBlur: false,
    };
  }
  const params = new URLSearchParams(globalThis.location.search);
  const rawThreshold = Number(params.get('scormThreshold'));
  return {
    enabled: params.get('scorm') === '1',
    disableSeek: params.get('disableSeek') === '1',
    threshold: rawThreshold > 0 && rawThreshold <= 1 ? rawThreshold : 1,
    pauseOnBlur: params.get('pauseOnBlur') === '1',
  };
}

export interface UseVideoPlayerSetupParams {
  mediaUrl: string | undefined;
  posterUrl?: string;
  isMobile: boolean;
  windowWidth: number;
  showDocumentAnimation: boolean;
  theme: CustomTheme;
  playerRef: React.MutableRefObject<Player | null>;
  pdfViewerRef: React.MutableRefObject<never>;
  setIsVideoLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setShowMobilePlayButton: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPauseOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  layout?: Layout;
  isPreview?: boolean;
  publicationId: string;
  projectId: string;
  organizationId?: string;
  publicationTitle: string;
}

export interface UseVideoPlayerSetupResult {
  memoizedVideoJsOptions: VideoJsOptions;
  handlePlayerReady: (player: Player) => void;
}

/**
 * Hook to setup video player with options, analytics, and event handlers
 */
export const useVideoPlayerSetup = ({
  mediaUrl,
  posterUrl,
  isMobile,
  windowWidth,
  showDocumentAnimation,
  theme,
  playerRef,
  pdfViewerRef,
  setIsVideoLoaded,
  setShowMobilePlayButton,
  setShowPauseOverlay,
  layout,
  isPreview,
  publicationId,
  projectId,
  organizationId,
  publicationTitle,
}: UseVideoPlayerSetupParams): UseVideoPlayerSetupResult => {
  const scormConfig = useMemo(() => readScormConfigFromUrl(), []);

  const memoizedVideoJsOptions = useMemo(
    (): VideoJsOptions => ({
      autoplay: false,
      muted: false,
      controls: !isMobile || (isMobile && !showDocumentAnimation),
      fluid: true,
      disablePictureInPicture: !isMobile,
      width: isMobile ? windowWidth : 400,
      sources: [
        {
          src: mediaUrl as string,
          type: 'video/mp4',
        },
      ],
      experimentalSvgIcons: true,
      // Omit poster when missing — video.js renders a broken-image icon for an empty string.
      ...(posterUrl ? { poster: posterUrl } : {}),
      controlBar: {
        fullscreenToggle: false,
        pictureInPictureToggle: false,
        // The speed menu stays unmounted in SCORM no-seek mode (its rates are
        // also locked to 1x via data-setup in VideoPlayer); it is re-added
        // imperatively once the lesson completes and seeking unlocks.
        playbackRateMenuButton: !scormConfig.disableSeek,
        remainingTimeDisplay: false,
        volumePanel: false,
        // Forward skip, progress bar and chapters stay MOUNTED in no-seek
        // mode but are hidden via the .vjs-no-seek class (see styles/app.css)
        // so completion can restore them by removing the class. The snap-back
        // guard in useScormTracking is the actual enforcement — unhiding the
        // controls via devtools still can't move past the watched point.
        skipButtons: { forward: 10, backward: 10 },
      },
    }),
    [
      mediaUrl,
      posterUrl,
      isMobile,
      windowWidth,
      showDocumentAnimation,
      scormConfig.disableSeek,
    ],
  );

  const { trackEvent } = useAnalytics({
    publicationId,
    projectId,
    organizationId,
  });

  // Fire publication_view exactly once, on the client only.
  // A useEffect (rather than a render side-effect) avoids calling
  // getTrackingProperties during SSR where sessionStorage is unavailable
  // and ensures compatibility with React 18 Strict Mode.
  // Skipped in preview mode (e.g. admin's CreatorPreview) so editors
  // previewing their own publication don't inflate view metrics.
  const analyticsTracked = useRef(false);
  useEffect(() => {
    if (isPreview || analyticsTracked.current) return;
    analyticsTracked.current = true;
    trackEvent(MediaAnalyticsEvents.PublicationView, {
      publication_title: publicationTitle,
    });
  }, [trackEvent, publicationTitle, isPreview]);

  const { handleUnload, setupPlayerTracking } = useVideoAnalytics({
    projectId,
    publicationId,
    organizationId,
  });

  // Once the lesson is completed (this session or a previous one), all seek
  // restrictions stop applying: drop the no-seek skin (progress bar, forward
  // skip and chapters reappear) and restore the playback-speed menu.
  const scormCompletedRef = useRef(false);
  const unlockSeekUi = useCallback((player: Player) => {
    player.removeClass('vjs-no-seek');
    try {
      const unlockable = player as unknown as {
        playbackRates?: (rates: number[]) => void;
        controlBar?: {
          getChild: (name: string) => unknown;
          addChild: (name: string) => unknown;
        };
      };
      unlockable.playbackRates?.(DEFAULT_PLAYBACK_RATES);
      if (!unlockable.controlBar?.getChild('PlaybackRateMenuButton')) {
        unlockable.controlBar?.addChild('PlaybackRateMenuButton');
      }
    } catch {
      // Player torn down mid-unlock; the next mount applies the unlocked UI.
    }
  }, []);

  const handleScormCompleted = useCallback(() => {
    scormCompletedRef.current = true;
    const player = playerRef.current;
    if (player && !player.isDisposed()) unlockSeekUi(player);
  }, [playerRef, unlockSeekUi]);

  // The SCORM hook owns the full watch-integrity logic: watched-coverage
  // tracking, completion, resume (lesson_location + suspend_data) and the
  // forward-seek lock — the lock and resume must share the same
  // "furthest watched" state, otherwise the resume seek gets snapped back.
  const { setupPlayerTracking: setupScormTracking } = useScormTracking({
    enabled: scormConfig.enabled,
    completionThreshold: scormConfig.threshold,
    disableSeek: scormConfig.disableSeek,
    onCompleted: handleScormCompleted,
  });

  useEffect(() => {
    // `pagehide` is the mobile-safe signal (iOS Safari skips `beforeunload`
    // when closing a tab). Both are registered so desktop and mobile are covered.
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [handleUnload]);

  // Opt-in (SCORM export option, `pauseOnBlur=1`): pause playback when the
  // player loses the user's attention — tab switched (visibilitychange) or
  // another window/app focused (blur — also fires when the user clicks the
  // surrounding LMS page). Off by default so the standard player keeps its
  // original keep-playing behavior.
  useEffect(() => {
    if (!scormConfig.pauseOnBlur) return;
    const pausePlayback = () => {
      const player = playerRef.current;
      if (!player || player.isDisposed?.()) return;
      try {
        if (!player.paused()) player.pause();
      } catch {
        // Player mid-teardown; nothing to pause.
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') pausePlayback();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('blur', pausePlayback);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('blur', pausePlayback);
    };
  }, [playerRef, scormConfig.pauseOnBlur]);

  const handlePlayerReady = useCallback(
    (player: Player) => {
      playerRef.current = player;
      setupPlayerTracking(player);
      setupScormTracking(
        player as unknown as Parameters<typeof setupScormTracking>[0],
      );

      if (scormConfig.disableSeek) {
        if (scormCompletedRef.current) {
          // Already completed (e.g. player remount after completion): mount
          // straight into the unlocked UI.
          unlockSeekUi(player);
        } else {
          // The .vjs-no-seek class hides the progress bar, forward-skip and
          // chapters menu, and unhides video.js's time displays as a
          // "current / duration" readout (styled in styles/app.css).
          player.addClass('vjs-no-seek');
        }
      }

      player.on('loadedmetadata', () => {
        const videoWidth = player.videoWidth();
        const videoHeight = player.videoHeight();
        const aspectRatio = videoWidth / videoHeight;
        (document.querySelector(':root') as HTMLElement)?.style?.setProperty(
          '--video-aspect-ratio',
          aspectRatio.toString(),
        );
        setIsVideoLoaded(true);
      });

      player.on('play', () => {
        setShowMobilePlayButton(false);
        setShowPauseOverlay(false);
        handleShowHeader(false);
      });

      player.on('pause', () => {
        setShowMobilePlayButton(true);
        setShowPauseOverlay(false);
        handleShowHeader(true);
      });

      player.on(
        'cuepoints:start',
        (
          _e: unknown,
          data: {
            index: number;
            cuepoint: Cuepoint;
            highlights: IHighlight[];
          },
        ) => {
          try {
            (pdfViewerRef?.current as any)?.scrollViewerTo(
              data.highlights.find((x) => x.id === data.cuepoint.highlightId),
            );
          } catch {
            // PDF viewer not fully initialized yet, ignore
          }
        },
      );
    },
    [
      setupPlayerTracking,
      setupScormTracking,
      scormConfig.disableSeek,
      unlockSeekUi,
      playerRef,
      pdfViewerRef,
      setIsVideoLoaded,
      setShowMobilePlayButton,
      setShowPauseOverlay,
    ],
  );

  // Layout effect for pausing/controlling player (legacy mode)
  useEffect(() => {
    if (layout === undefined) return; // new mode doesn't use layout
    if (layout === 'chat') {
      setTimeout(() => {
        playerRef.current?.pause();
        playerRef.current?.controls(false);
      }, 10);
    } else {
      playerRef.current?.controls(true);
    }
  }, [layout, playerRef]);

  // Theme CSS variables effect
  useEffect(() => {
    (document.querySelector(':root') as HTMLElement)?.style?.setProperty(
      '--primary-color',
      theme.colors.primary,
    );
    (document.querySelector(':root') as HTMLElement)?.style?.setProperty(
      '--secondary-color',
      theme.colors.secondary,
    );
    (document.querySelector(':root') as HTMLElement)?.style?.setProperty(
      '--ctaText-color',
      theme.colors.ctaTextColor,
    );
    (document.querySelector(':root') as HTMLElement)?.style?.setProperty(
      '--font-family',
      theme.fontFamily,
    );
  }, [theme]);

  return {
    memoizedVideoJsOptions,
    handlePlayerReady,
  };
};
